import React, { useState, useEffect, useRef  } from 'react';
import { TrendingUp, Calendar, BarChart3, Target, Clock, Trophy, Medal, Award, HelpCircle  } from 'lucide-react';
import api from '../services/api';
import { getSpecializationConfigs } from '../config/fundConfigurations';
import TooltipPortal from '../components/TooltipPortal';
import { getDisplayName } from '../config/fundNamesConfig';
import html2canvas from 'html2canvas';
import './styles/returnsSection.css';
const getDateRanges = (lastReportDate = null) => {
  // פונקציה לחילוץ תאריך מתקופת דיווח (פורמט YYYYMM)
  const parseReportPeriod = (reportPeriod) => {
    if (!reportPeriod) return new Date();
    
    const periodStr = reportPeriod.toString();
    if (periodStr.length === 6) {
      const year = parseInt(periodStr.slice(0, 4));
      const month = parseInt(periodStr.slice(4, 6)) - 1; // JavaScript months are 0-based
      return new Date(year, month);
    }
    
    return new Date(reportPeriod);
  };

  // פונקציה לפורמט תאריך לעברית
  const formatHebrewDate = (date) => {
    const months = [
      'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
      'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // פונקציה לפורמט טווח תאריכים (MM/YY - MM/YY)
  const formatDateRange = (startDate, endDate) => {
    const startMonth = startDate.getMonth() + 1;
    const startYear = startDate.getFullYear();
    const endMonth = endDate.getMonth() + 1;
    const endYear = endDate.getFullYear();
    
    return `${endMonth.toString().padStart(2, '0')}/${endYear.toString().slice(-2)} - ${startMonth.toString().padStart(2, '0')}/${startYear.toString().slice(-2)}`;
  };

  // התאריך הבסיסי הוא תקופת הדיווח האחרונה
  const baseDate = parseReportPeriod(lastReportDate);
  const currentYear = baseDate.getFullYear();
  const currentMonth = baseDate.getMonth();

  return {
    // חודש אחרון - תקופת הדיווח עצמה
    month1: {
      display: formatHebrewDate(baseDate),
      range: formatHebrewDate(baseDate)
    },
    
    // מתחילת השנה - מינואר של השנה הנוכחית עד תקופת הדיווח
    ytd: {
      display: ` ${(currentMonth + 1).toString().padStart(2, '0')}/${currentYear.toString().slice(-2)}- 01/${currentYear.toString().slice(-2)}`,
      range: `מתחילת ${currentYear} עד ${formatHebrewDate(baseDate)}`
    },
    
    // שנה - 12 חודשים אחורה מתקופת הדיווח
    year1: {
      display: formatDateRange(
        new Date(currentYear - 1, currentMonth + 1), 
        baseDate
      ),
      range: 'שנה אחרונה'
    },
    
    // שנתיים - 24 חודשים אחורה מתקופת הדיווח
    year2: {
      display: formatDateRange(
        new Date(currentYear - 2, currentMonth + 1), 
        baseDate
      ),
      range: 'שנתיים אחרונות'
    },
    
    // 3 שנים - 36 חודשים אחורה מתקופת הדיווח
    year3: {
      display: formatDateRange(
        new Date(currentYear - 3, currentMonth + 1), 
        baseDate
      ),
      range: '3 שנים אחרונות'
    },
    
    // 4 שנים - 48 חודשים אחורה מתקופת הדיווח
    year4: {
      display: formatDateRange(
        new Date(currentYear - 4, currentMonth + 1), 
        baseDate
      ),
      range: '4 שנים אחרונות'
    },
    
    // 5 שנים - 60 חודשים אחורה מתקופת הדיווח
    year5: {
      display: formatDateRange(
        new Date(currentYear - 5, currentMonth + 1), 
        baseDate
      ),
      range: '5 שנים אחרונות'
    }
  };
};
const ReturnsSection = ({ 
  product, 
  category, 
  currentProductId , matchingSpecialization,lastUpdated
}) => {
  const [rankings, setRankings] = useState({});
  const [loadingRankings, setLoadingRankings] = useState(true);
  const [comparisonData, setComparisonData] = useState([]);
  const [bestMatchingSpec, setBestMatchingSpec] = useState(null);
  const [animationTrigger, setAnimationTrigger] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const returnsSectionRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
const dateRanges = getDateRanges(product.reportPeriod || lastUpdated);
  // 🔥 כל ה-useEffect hooks חייבים להיות לפני כל return!
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

useEffect(() => {
  if (product && category && product.returns && bestMatchingSpec) {
    const timer = setTimeout(() => {
      calculateRankings();
    }, 100);
    return () => clearTimeout(timer);
  }
}, [product, category, currentProductId, bestMatchingSpec]);
  // וגם שנה את ה-useEffect של האנימציה:
  useEffect(() => {
    if (!loadingRankings && Object.keys(rankings).length > 0) {
      const timer = setTimeout(() => setAnimationTrigger(true), 300);
      return () => clearTimeout(timer);
    }
  }, [loadingRankings, rankings]);
useEffect(() => {
  if (matchingSpecialization) {
    setBestMatchingSpec(matchingSpecialization);
  } else if (product && category) {
    const calculatedSpec = getBestMatchingSpecialization(category, product);
    setBestMatchingSpec(calculatedSpec);
  }
}, [matchingSpecialization, product, category]);
  // 🆕 עכשיו הבדיקות - אחרי כל ה-hooks!
  if (!product || !category || !currentProductId) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 mb-8 relative overflow-hidden">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-3 md:gap-4 xl:gap-3">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="bg-gray-200 rounded-xl h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 🆕 בדיקה נוספת לנתוני תשואות
  if (!product.returns) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 mb-8 relative overflow-hidden">
        <div className="text-center py-8">
          <div className="text-gray-500 mb-4">אין נתוני תשואות זמינים עבור מוצר זה</div>
        </div>
      </div>
    );
  }

  // חישוב דירוג לפי הקבוצה של 10 הקרנות
  const calculateRankings = async () => {
    try {
      setLoadingRankings(true);
      setAnimationTrigger(false);
      

      
      if (!bestMatchingSpec || !bestMatchingSpec.fundIds.length) {
        setLoadingRankings(false);
        return;
      }

      const allProducts = await api.getProducts(category);
      const groupProducts = allProducts.filter(p => 
        bestMatchingSpec.fundIds.includes(parseInt(p.id))
      );

      const foundIds = groupProducts.map(p => parseInt(p.id));
      const missingIds = bestMatchingSpec.fundIds.filter(id => !foundIds.includes(parseInt(id)));
      
      const additionalProducts = [];
      for (const missingId of missingIds) {
        try {
          const missingProduct = await api.getProductById(missingId.toString(), category);
          if (missingProduct) {
            additionalProducts.push(missingProduct);
          }
        } catch (error) {
          console.warn(`❌ Failed to load product ${missingId}:`, error.message);
        }
      }

      const fullGroupProducts = [...groupProducts, ...additionalProducts];
      setComparisonData(fullGroupProducts);

      const newRankings = {};
      const periods = ['month1', 'ytd', 'year1', 'year2', 'year3', 'year4', 'year5'];

      periods.forEach(period => {
        const validProducts = fullGroupProducts.filter(p => 
          p.returns && 
          p.returns[period] !== null && 
          p.returns[period] !== undefined && 
          !isNaN(p.returns[period])
        );

        if (validProducts.length === 0) {
          newRankings[period] = null;
          return;
        }

        const sorted = validProducts.sort((a, b) => 
          (b.returns[period] || 0) - (a.returns[period] || 0)
        );

        const currentProductIndex = sorted.findIndex(p => 
          parseInt(p.id) === parseInt(currentProductId)
        );

        if (currentProductIndex !== -1) {
          newRankings[period] = {
            rank: currentProductIndex + 1,
            total: validProducts.length,
            value: sorted[currentProductIndex].returns[period],
            bestValue: sorted[0].returns[period],
            worstValue: sorted[sorted.length - 1].returns[period]
          };
        } else {
          newRankings[period] = null;
        }
      });

      setRankings(newRankings);

    } catch (error) {
      console.error('❌ Error calculating rankings:', error);
    } finally {
      setLoadingRankings(false);
    }
  };

  const getBestMatchingSpecialization = (category, currentProduct) => {
    const specializations = getSpecializationConfigs(category);
    const currentProductId = parseInt(currentProduct.id);
    
    let matchingSpec = specializations.find(spec => 
      spec.fundIds.includes(currentProductId)
    );
    
    if (matchingSpec) {
      return matchingSpec;
    }

    console.log(`🔍 Product ${currentProductId} not in any list, trying smart detection...`);
    matchingSpec = detectSpecializationFromName(currentProduct.name, specializations);
    
    if (matchingSpec) {
      const isGoodMatch = currentProduct.name.toLowerCase().includes(matchingSpec.key.toLowerCase()) ||
                         matchingSpec.key === 'כללי';
      
      if (!isGoodMatch) {
        console.log(`⚠️ Match quality seems low for "${currentProduct.name}" -> ${matchingSpec.key}`);
      }
      
      return {
        ...matchingSpec,
        title: `${matchingSpec.title} (כולל ${currentProduct.name})`,
        fundIds: [currentProductId, ...matchingSpec.fundIds].slice(0, 10),
        isExtended: true
      };
    }

    const fallbackSpec = specializations.find(s => s.key.includes('כללי')) || specializations[0];
    
    if (fallbackSpec) {
      return {
        ...fallbackSpec,
        title: `השוואה כללית (כולל ${currentProduct.name})`,
        fundIds: [currentProductId, ...fallbackSpec.fundIds].slice(0, 10),
        isExtended: true,
        isFallback: true
      };
    }

    return {
      key: 'custom',
      title: `השוואה עם ${currentProduct.name}`,
      fundIds: [currentProductId],
      isExtended: true,
      isFallback: true
    };
  };

  const detectSpecializationFromName = (productName, specializations) => {
    const name = productName.toLowerCase();
    
    const keywords = {
      'כללי': ['כללי', 'מאוזן', 'מעורב', 'בסיס', 'אסטרטגי'],
      'sp500': ['s&p 500', 'sp500', 's&p500', 'אס אנד פי', 'אס&פי'],
      'מניות': ['מניות', 'equity', 'stocks', 'מניתי', 'צמיחה'],
      'אג"ח-ממשלות': ['אגח ממשל', 'ממשלתי', 'government', 'treasury', 'אגרות חוב ממשלתי'],
      'אשראי-ואגח': ['אשראי', 'אגח', 'bonds', 'credit', 'קונצרני', 'חוב קונצרני'],
      'הלכתי': ['הלכתי', 'שרעי', 'halal', 'kosher', 'religious', 'דתי'],
      'עד-50': ['עד 50', 'לבני 50 ומטה', 'עד גיל 50', 'צעיר', 'צעירים'],
      '50-60': ['50-60', '50 עד 60', 'בוגר', 'בוגרים', 'בינוני'],
      'מעל-60': ['מעל 60', 'לבני 60 ומעלה', 'וותיק', 'senior', 'וותיקים']
    };
    
    const scores = specializations.map(spec => {
      const specKeywords = keywords[spec.key] || [];
      let score = 0;
      
      specKeywords.forEach(keyword => {
        if (name.includes(keyword.toLowerCase())) {
          score += keyword.length;
        }
      });
      
      return { spec, score };
    });
    
    const bestMatch = scores
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)[0];
    
    if (bestMatch) {
      console.log(`🎯 Best match for "${productName}": ${bestMatch.spec.key} (score: ${bestMatch.score})`);
      return bestMatch.spec;
    }
    
    console.log(`🤷 No good match found for "${productName}", using general category`);
    return specializations[0] || null;
  };

  // קבלת רמת הביצועים לפי דירוג
  const getPerformanceLevel = (ranking) => {
    if (!ranking) return null;
    
    const { rank } = ranking;
    
    if (rank <= 2) return { level: 'excellent', label: 'מצטיין', color: 'emerald' };
    if (rank <= 4) return { level: 'good', label: 'טוב', color: 'green' };
    if (rank <= 6) return { level: 'average', label: 'ממוצע', color: 'yellow' };
    if (rank <= 8) return { level: 'below', label: 'מתחת לממוצע', color: 'orange' };
    return { level: 'poor', label: 'חלש', color: 'red' };
  };

  // קבלת צבעי הכרטיס לפי רמת הביצועים
  const getCardColors = (ranking) => {
    const performance = getPerformanceLevel(ranking);
    if (!performance) return {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      accent: 'bg-gray-400',
      text: 'text-gray-600'
    };

    const colorMap = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        accent: 'bg-emerald-500',
        text: 'text-emerald-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        accent: 'bg-green-500',
        text: 'text-green-700'
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        accent: 'bg-yellow-500',
        text: 'text-yellow-700'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        accent: 'bg-orange-500',
        text: 'text-orange-700'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        accent: 'bg-red-500',
        text: 'text-red-700'
      }
    };

    return colorMap[performance.color];
  };

  // פורמט ערך
  const formatValue = (value, suffix = '%', decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    
    const numValue = parseFloat(value);
    const formattedNumber = Math.abs(numValue).toFixed(decimals);
    
    if (numValue < 0) {
      return `-${formattedNumber}${suffix}`;
    }
    
    return `${formattedNumber}${suffix}`;
  };

  // לגנדת הצבעים
// החלף את הלגנדה בגרסה המצומצמת הזאת:
const ColorLegend = () => {
  const levels = [
    { color: 'emerald', label: 'מצטיין', range: '1-2' },
    { color: 'green', label: 'טוב', range: '3-4' },
    { color: 'yellow', label: 'ממוצע', range: '5-6' },
    { color: 'orange', label: 'מתחת לממוצע', range: '7-8' },
    { color: 'red', label: 'חלש', range: '9+' }
  ];

  // קבלת הרמות הפעילות בנתונים הנוכחיים
  const activeLevels = new Set();
  Object.values(rankings).forEach(ranking => {
    if (ranking) {
      const performance = getPerformanceLevel(ranking);
      if (performance) activeLevels.add(performance.color);
    }
  });

  return (
    <div className="mb-3 md:mb-6 relative">
      {/* רקע עם gradient ואפקטים */}
      <div className="relative bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-purple-50/80 rounded-lg md:rounded-xl border border-blue-100/50 shadow-sm backdrop-blur-sm overflow-hidden">
        
        {/* אפקט זוהר רקע */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/5 via-purple-400/5 to-indigo-400/5"></div>
        
        {/* תוכן */}
        <div className="relative p-2 md:p-4">
          
          {/* כותרת עם אייקון מעוצב - מצומצמת במובייל */}
          <div className="mb-1 md:mb-1">
            <div className="flex items-center justify-center md:justify-start gap-1 md:gap-2">
              <div className="flex items-center justify-center w-5 h-5 md:w-8 md:h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-md md:rounded-lg shadow-lg">
                <Trophy className="h-2.5 w-2.5 md:h-4 md:w-4 text-white" />
              </div>
              <div>
                <h3 className="text-xs md:text-base font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  <span className="hidden sm:inline">דירוג ביצועים לטווח של עד 5 שנים אחרונות</span>
                  <span className="sm:hidden">דירוג ביצועים לטווח של עד 5 שנים אחרונות </span>
                </h3>
              </div>
            </div>
          </div>
          
          {/* עיצוב מובייל - קומפקטי מאוד */}
 
<div className="md:hidden">
  <div className="flex justify-between items-stretch gap-0.5 bg-white/60 backdrop-blur-sm rounded-md p-1 border border-white/40 shadow-inner">
    {levels.map((level, index) => {
      const isActive = activeLevels.has(level.color);
      const colorClasses = {
        emerald: { 
          gradient: 'from-emerald-400 to-emerald-600',
          glow: 'shadow-emerald-400/30' 
        },
        green: { 
          gradient: 'from-green-400 to-green-600',
          glow: 'shadow-green-400/30' 
        },
        yellow: { 
          gradient: 'from-yellow-400 to-yellow-600',
          glow: 'shadow-yellow-400/30' 
        },
        orange: { 
          gradient: 'from-orange-400 to-orange-600',
          glow: 'shadow-orange-400/30' 
        },
        red: { 
          gradient: 'from-red-400 to-red-600',
          glow: 'shadow-red-400/30' 
        }
      };

      return (
 <div 
  key={level.color}
  className={`flex flex-col items-center gap-0.5 p-1 rounded-md transition-all duration-500 flex-1 group relative ${
    isActive 
      ? 'bg-white/90 border border-blue-200/50 shadow-md scale-105 transform-gpu' 
      : 'bg-transparent hover:bg-white/30'
  }`}
>

{/* הבהוב עדין לכל הריבוע + הבהוב על העיגול */}
{isActive && (
  <div className={`absolute inset-0 rounded-md bg-gradient-to-r ${colorClasses[level.color].gradient} opacity-5 animate-pulse`}></div>
)}
  
<div className={`relative w-1.5 h-1.5 rounded-full bg-gradient-to-br ${colorClasses[level.color].gradient} transition-all duration-300 z-10 ${
  isActive 
    ? `ring-1 ring-white ring-offset-1 ring-offset-blue-100 shadow-sm ${colorClasses[level.color].glow}` 
    : 'group-hover:scale-110'
}`}>
  {/* הבהוב על העיגול - בדיוק כמו בדקסטופ */}
  {isActive && (
    <>
      <div className="absolute inset-0 rounded-full bg-white/40 animate-ping"></div>
      <div className="absolute inset-0.5 rounded-full bg-white/20 animate-pulse"></div>
    </>
  )}
</div>
  
  <div className="text-center relative z-10">
    <div className={`text-xs font-semibold leading-tight transition-colors duration-300 ${
      isActive ? 'text-gray-800' : 'text-gray-600'
    }`} style={{ fontSize: '0.65rem' }}>
      {level.label}
    </div>
    <div className={`leading-tight transition-colors duration-300 ${
      isActive ? 'text-blue-600 font-medium' : 'text-gray-500'
    }`} style={{ fontSize: '0.55rem' }}>
      {level.range}
    </div>
  </div>
</div>
      );
    })}
  </div>
</div>

          {/* עיצוב דקסטופ - ללא שינוי */}
          <div className="hidden md:flex flex-wrap gap-2 lg:gap-3 justify-center">
            {levels.map((level, index) => {
              const isActive = activeLevels.has(level.color);
              const colorClasses = {
                emerald: { 
                  gradient: 'from-emerald-400 to-emerald-600',
                  glow: 'shadow-emerald-400/40',
                },
                green: { 
                  gradient: 'from-green-400 to-green-600',
                  glow: 'shadow-green-400/40',
                },
                yellow: { 
                  gradient: 'from-yellow-400 to-yellow-600',
                  glow: 'shadow-yellow-400/40',
                },
                orange: { 
                  gradient: 'from-orange-400 to-orange-600',
                  glow: 'shadow-orange-400/40',
                },
                red: { 
                  gradient: 'from-red-400 to-red-600',
                  glow: 'shadow-red-400/40',
                }
              };

              return (
                <div 
                  key={level.color}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-500 cursor-default transform-gpu ${
                    isActive 
                      ? 'bg-white/90 backdrop-blur-sm border-2 border-blue-200/50 shadow-xl scale-105 -translate-y-0.5' 
                      : 'bg-white/40 backdrop-blur-sm border border-white/30 hover:bg-white/60 hover:scale-102 hover:-translate-y-0.5 hover:shadow-lg'
                  }`}
                >
                  <div className="relative">
                    <div className={`w-4 h-4 rounded-full bg-gradient-to-br ${colorClasses[level.color].gradient} transition-all duration-300 ${
                      isActive 
                        ? `ring-2 ring-white ring-offset-1 ring-offset-blue-100 shadow-lg ${colorClasses[level.color].glow}` 
                        : 'group-hover:scale-110 group-hover:shadow-md'
                    }`}>
                      {isActive && (
                        <>
                          <div className="absolute inset-0 rounded-full bg-white/40 animate-ping"></div>
                          <div className="absolute inset-0.5 rounded-full bg-white/20 animate-pulse"></div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-sm">
                    <div className={`font-bold transition-colors duration-300 ${
                      isActive 
                        ? 'text-gray-800' 
                        : 'text-gray-600 group-hover:text-gray-700'
                    }`}>
                      {level.label}
                    </div>
                    <div className={`text-xs transition-colors duration-300 ${
                      isActive 
                        ? 'text-blue-600 font-semibold' 
                        : 'text-gray-500 group-hover:text-gray-600'
                    }`}>
                      מקום {level.range}
                    </div>
                  </div>

                  {/* אפקט זוהר לרמות פעילות */}
                  {isActive && (
                    <div className={`absolute inset-0 rounded-lg bg-gradient-to-r ${colorClasses[level.color].gradient} opacity-5 animate-pulse`}></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// החלף את ה-PeriodCard הנוכחי שלך בזה:
const PeriodCard = ({ period, title, index, ranking, returnValue }) => {
  const performance = getPerformanceLevel(ranking);
   const dateRange = dateRanges[period];
  const cardStyle = {
    animationDelay: `${index * 100}ms`,
    transform: animationTrigger ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
    opacity: animationTrigger ? 1 : 0,
    transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  };

  // בחירת קלאס הצבע לפי ביצועים
  const performanceClass = performance ? performance.level : '';
  
  // צבעי הטקסט התחתון לפי ביצועים - מתאים לצבעי ה-colorMap
  const getTextColor = () => {
    if (!performance) return 'text-gray-600';
    
    const colorMap = {
      excellent: 'text-emerald-700',  // emerald-700
      good: 'text-green-700',        // green-700  
      average: 'text-yellow-700',    // yellow-700
      below: 'text-orange-700',      // orange-700
      poor: 'text-red-700'           // red-700
    };
    
    return colorMap[performance.level] || 'text-gray-600';
  };

  return (
    <div className="group w-full max-w-[140px] mx-auto" style={cardStyle}>
      {/* קונטיינר מקובע לכל הכרטיסיה */}
      <div className="flex flex-col items-center">
           <div className="mb-3 md:mb-4 text-center w-full px-1">
          <div className="text-xs font-medium text-blue-600 leading-tight">
            ({dateRange?.display})
          </div>
        </div>
        {/* העיגול */}
        <div className={`neon-card ${performanceClass}`}>
          <div className="neon-card-content">
            <h3>{title}</h3>
            
            <div className="value" dir="ltr">
              {formatValue(returnValue)}
            </div>

            {ranking && !loadingRankings && (
               <div className="rank-badge zoom-pulse">
                {ranking.rank} מתוך {ranking.total}
              </div>
            )}

            {loadingRankings && (
              <div className="rank-badge animate-pulse">
                ...
              </div>
            )}
          </div>
        </div>
        
        {/* הטקסט מתחת - במיקום קבוע */}
        <div className="mt-3 md:mt-4 w-full">
          {ranking && !loadingRankings && performance && ranking.total > 1 && (
            <div className="text-center w-full">
              <span className={`text-sm md:text-base font-semibold ${getTextColor()} block leading-tight whitespace-nowrap`}>
                {performance.label}
              </span>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};


const TooltipContent = ({ isMobile = false, onClose }) => (
  <div className={`relative ${isMobile ? 'bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200' : ''}`}>
    {/* 🆕 כפתור סגירה */}
    {onClose && (
      <button
        onClick={onClose}
        className="absolute top-2 left-2 text-gray-500 hover:text-red-500 transition-colors text-lg font-bold leading-none w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50"
        aria-label="סגור"
      >
        ×
      </button>
    )}
    
    <h4 className={`font-semibold mb-3 ${isMobile ? 'text-gray-800' : 'text-white'}`}>
      מערכת דירוג הביצועים
    </h4>
    <p className={`text-sm mb-3 ${isMobile ? 'text-gray-700' : 'text-gray-300'}`}>
      הדירוג מבוסס על השוואה ל-{comparisonData.length} קרנות נבחרות באותה רמת סיכון וקטגוריה.
    </p>
    <p className={`text-xs italic ${isMobile ? 'text-gray-600' : 'text-gray-400'}`}>
      ככל שהמקום נמוך יותר (קרוב ל-1), הביצועים טובים יותר יחסית לקבוצת הקרנות.
    </p>
  </div>
);

  // פונקציה לייצוא התמונה
  const exportReturnsAsImage = async () => {
    if (!returnsSectionRef.current) {
      alert('שגיאה בטעינת הנתונים');
      return;
    }

    try {
      // הסתר את ההודעה "מייצא..." לפני הצילום - לא להציג אותה בכלל
      setExportMessage('');
      
      // שמור מצב מובייל/דסקטופ
      const isCurrentlyMobile = window.innerWidth < 768;
      const originalWidth = returnsSectionRef.current.style.width;
      const originalMinWidth = returnsSectionRef.current.style.minWidth;
      const originalMaxWidth = returnsSectionRef.current.style.maxWidth;
      
      // אם זה מובייל - שמור את הרוחב המקורי, אחרת קבע 1200px
      if (!isCurrentlyMobile) {
        returnsSectionRef.current.style.width = '1200px';
        returnsSectionRef.current.style.minWidth = '1200px';
        returnsSectionRef.current.style.maxWidth = '1200px';
      }
      
      // יצירת כותרת זמנית - לוגו בצד ימין, שם מסלול ורמת סיכון באמצע
      const headerElement = document.createElement('div');
      headerElement.style.cssText = 'margin-bottom: 2rem; padding: 2rem 1rem; display: flex; flex-direction: row; align-items: center; justify-content: center; position: relative; border-bottom: 1px solid #e5e7eb;';
      
      // לוגו החברה - בצד ימין (בגלל RTL, צריך להשתמש ב-right: 0)
      if (product.managingCorpLegalId) {
        const logoImg = document.createElement('img');
        logoImg.src = `/logos/${product.managingCorpLegalId}.png`;
        logoImg.alt = product.company || 'לוגו חברה';
        // במובייל - לוגו קטן יותר כדי שלא יעלה על הטקסט
        const logoSize = isCurrentlyMobile ? '80px' : '120px';
        logoImg.style.cssText = `height: ${logoSize}; width: ${logoSize}; object-fit: contain; flex-shrink: 0; position: absolute; right: 0;`;
        logoImg.onerror = () => { logoImg.style.display = 'none'; };
        headerElement.appendChild(logoImg);
      }
      
      // מיכל לטקסט (שם הקרן ורמת סיכון) - באמצע
      // במובייל - הוסף padding משני הצדדים כדי שהלוגו לא יעלה על הטקסט אבל הטקסט יישאר במרכז
      const textContainerPadding = isCurrentlyMobile ? 'padding-left: 90px; padding-right: 90px;' : '';
      const textContainer = document.createElement('div');
      textContainer.style.cssText = `flex: 1; text-align: center; ${textContainerPadding}`;
      
      // שם הקרן
      const fundName = document.createElement('div');
      fundName.style.cssText = 'font-size: 1.25rem; font-weight: 600; color: #111827; margin-bottom: 0.5rem;';
      fundName.textContent = getDisplayName(product);
      textContainer.appendChild(fundName);
      
      // רמת סיכון - מלל פשוט
      if (product.riskLevel) {
        const riskDiv = document.createElement('div');
        riskDiv.style.cssText = 'font-size: 1rem; color: #374151;';
        riskDiv.textContent = `רמת סיכון: ${product.riskLevel.level} מתוך 5`;
        textContainer.appendChild(riskDiv);
      }
      
      headerElement.appendChild(textContainer);
      
      // הוסף את הכותרת בתחילת הקומפוננטה
      const firstChild = returnsSectionRef.current.querySelector('.relative.z-10');
      if (firstChild) {
        firstChild.insertBefore(headerElement, firstChild.firstChild);
      }
      
      // מצא את כל האלמנטים עם טקסט חתוך
      const truncatedElements = returnsSectionRef.current.querySelectorAll('.truncate, [class*="truncate"]');
      const originalStyles = [];
      
      // בטל את החיתוך זמנית
      truncatedElements.forEach(el => {
        originalStyles.push({
          element: el,
          overflow: el.style.overflow,
          textOverflow: el.style.textOverflow,
          whiteSpace: el.style.whiteSpace,
          maxWidth: el.style.maxWidth
        });
        
        el.style.overflow = 'visible';
        el.style.textOverflow = 'clip';
        el.style.whiteSpace = 'normal';
        el.style.maxWidth = 'none';
      });
      
      // הגדל את הרווח של הכותרת הראשית "תחקור תשואות" בייצוא
      const mainTitleContainer = returnsSectionRef.current.querySelector('h2');
      const originalMainTitleStyles = [];
      if (mainTitleContainer && mainTitleContainer.textContent?.includes('תחקור תשואות')) {
        const titleDiv = mainTitleContainer.closest('div');
        if (titleDiv) {
          const computedStyle = window.getComputedStyle(titleDiv);
          originalMainTitleStyles.push({
            element: titleDiv,
            marginBottom: titleDiv.style.marginBottom
          });
          titleDiv.style.marginBottom = '2.5rem'; // 40px - גדול יותר
        }
        
        // גם על ה-h2 עצמו
        const computedH2Style = window.getComputedStyle(mainTitleContainer);
        originalMainTitleStyles.push({
          element: mainTitleContainer,
          marginBottom: mainTitleContainer.style.marginBottom
        });
        mainTitleContainer.style.marginBottom = '1.5rem'; // 24px - גדול יותר
      }
      
      // תיקון צבע הכותרת "דירוג ביצועים" - שנה לשחור וודא שהיא נראית
      const rankingTitles = returnsSectionRef.current.querySelectorAll('h3');
      const originalTitleStyles = [];
      rankingTitles.forEach(title => {
        if (title.textContent?.includes('דירוג ביצועים')) {
          originalTitleStyles.push({
            element: title,
            color: title.style.color,
            background: title.style.background,
            webkitBackgroundClip: title.style.webkitBackgroundClip,
            backgroundClip: title.style.backgroundClip,
            webkitTextFillColor: title.style.webkitTextFillColor,
            className: title.className,
            zIndex: title.style.zIndex
          });
          // שנה לשחור וודא שהיא נראית
          title.style.color = '#000000';
          title.style.background = 'none';
          title.style.webkitBackgroundClip = 'unset';
          title.style.backgroundClip = 'unset';
          title.style.webkitTextFillColor = '#000000';
          title.style.zIndex = '10';
          // הסר את ה-class שמכיל gradient
          title.className = title.className.replace(/bg-gradient-to-r|bg-clip-text|text-transparent/g, '').trim();
          
          // גם על ה-span הפנימי אם יש
          const span = title.querySelector('span');
          if (span) {
            span.style.color = '#000000';
            span.style.webkitTextFillColor = '#000000';
            span.className = span.className.replace(/bg-gradient-to-r|bg-clip-text|text-transparent/g, '').trim();
          }
        }
      });
      
      // תיקון צבעים בעיגולים - הוסף רקע צבעוני ו-border מוצק
      const neonCards = returnsSectionRef.current.querySelectorAll('.neon-card');
      const originalCardStyles = [];
      const originalValueStyles = [];
      const originalRankBadgeStyles = [];
      const styleElements = [];
      
      neonCards.forEach(card => {
        const performanceClass = card.className.match(/excellent|good|average|below|poor/)?.[0];
        if (performanceClass) {
          originalCardStyles.push({
            element: card,
            border: card.style.border,
            borderWidth: card.style.borderWidth,
            borderStyle: card.style.borderStyle,
            borderColor: card.style.borderColor,
            boxShadow: card.style.boxShadow,
            background: card.style.background,
            backgroundColor: card.style.backgroundColor
          });
          
          // הוסף border מוצק ורקע צבעוני לפי רמת ביצועים
          const colorMap = {
            excellent: { 
              border: '#10b981', 
              shadow: '0 0 15px rgba(16, 185, 129, 0.4)', 
              textColor: '#047857',
              bgColor: 'rgba(16, 185, 129, 0.1)'
            },
            good: { 
              border: '#22c55e', 
              shadow: '0 0 15px rgba(34, 197, 94, 0.4)', 
              textColor: '#15803d',
              bgColor: 'rgba(34, 197, 94, 0.1)'
            },
            average: { 
              border: '#eab308', 
              shadow: '0 0 15px rgba(234, 179, 8, 0.4)', 
              textColor: '#a16207',
              bgColor: 'rgba(234, 179, 8, 0.1)'
            },
            below: { 
              border: '#f97316', 
              shadow: '0 0 15px rgba(249, 115, 22, 0.4)', 
              textColor: '#c2410c',
              bgColor: 'rgba(249, 115, 22, 0.1)'
            },
            poor: { 
              border: '#ef4444', 
              shadow: '0 0 15px rgba(239, 68, 68, 0.4)', 
              textColor: '#dc2626',
              bgColor: 'rgba(239, 68, 68, 0.1)'
            }
          };
          
          const colors = colorMap[performanceClass];
          if (colors) {
            card.style.border = `3px solid ${colors.border}`;
            card.style.boxShadow = colors.shadow;
            card.style.backgroundColor = colors.bgColor;
            
            // תיקון צבע הטקסט בעיגול
            const valueElement = card.querySelector('.value');
            if (valueElement) {
              originalValueStyles.push({
                element: valueElement,
                color: valueElement.style.color
              });
              valueElement.style.color = colors.textColor;
            }
            
            // הסר את המסגרת מה-rank-badge רק בתמונה המיוצאת
            const rankBadge = card.querySelector('.rank-badge');
            if (rankBadge) {
              originalRankBadgeStyles.push({
                element: rankBadge,
                position: rankBadge.style.position,
                transform: rankBadge.style.transform,
                marginTop: rankBadge.style.marginTop,
                marginBottom: rankBadge.style.marginBottom,
                background: rankBadge.style.background,
                backgroundColor: rankBadge.style.backgroundColor,
                border: rankBadge.style.border,
                borderWidth: rankBadge.style.borderWidth,
                borderStyle: rankBadge.style.borderStyle,
                borderColor: rankBadge.style.borderColor,
                borderRadius: rankBadge.style.borderRadius,
                padding: rankBadge.style.padding,
                color: rankBadge.style.color,
                fontWeight: rankBadge.style.fontWeight
              });
              rankBadge.style.position = 'relative';
              rankBadge.style.transform = 'none';
              rankBadge.style.marginTop = '0.3rem';
              rankBadge.style.marginBottom = '0';
              // הסר את המסגרת והרקע
              rankBadge.style.background = 'transparent';
              rankBadge.style.backgroundColor = 'transparent';
              rankBadge.style.border = 'none';
              rankBadge.style.borderWidth = '0';
              rankBadge.style.borderStyle = 'none';
              rankBadge.style.borderColor = 'transparent';
              rankBadge.style.borderRadius = '0';
              rankBadge.style.padding = '0';
              // שפר את הצבע והבולטות של הטקסט בלי המסגרת
              rankBadge.style.color = '#374151';
              rankBadge.style.fontWeight = '600';
            }
          }
        }
      });
      
      // הסתר את כל ה-::before gradients עם style element אחד
      const hideGradientsStyle = document.createElement('style');
      hideGradientsStyle.textContent = `
        .neon-card::before { display: none !important; }
        .neon-card::after { display: none !important; }
      `;
      document.head.appendChild(hideGradientsStyle);
      styleElements.push(hideGradientsStyle);
      
      // הסתר כפתורי פעולה זמנית
      const actionButtons = returnsSectionRef.current.querySelectorAll('button');
      const hiddenElements = [];
      
      actionButtons.forEach(btn => {
        hiddenElements.push({ element: btn, visibility: btn.style.visibility });
        btn.style.visibility = 'hidden';
      });

      // הוסף רקע לבן לכל האלמנטים חוץ מהעיגולים והאזור של הדירוג ביצועים
      const originalBackgrounds = [];
      const originalPerformanceLabelStyles = [];
      
      // כפיית רקע לבן רק על הלגנדה (מצטיין, טוב, ממוצע) - לא על העיגולים
      const legendContainer = returnsSectionRef.current.querySelector('[class*="bg-gradient-to-br"][class*="from-blue-50"]');
      
      if (legendContainer) {
        originalBackgrounds.push({
          element: legendContainer,
          background: legendContainer.style.background,
          backgroundColor: legendContainer.style.backgroundColor,
          backgroundImage: legendContainer.style.backgroundImage
        });
        legendContainer.style.background = 'white';
        legendContainer.style.backgroundColor = 'white';
        legendContainer.style.backgroundImage = 'none';
        
        // גם על כל האלמנטים בתוך הלגנדה
        const legendElements = legendContainer.querySelectorAll('*');
        legendElements.forEach(el => {
          const computedStyle = window.getComputedStyle(el);
          const bgImage = computedStyle.backgroundImage;
          const bgColor = computedStyle.backgroundColor;
          
          // אם יש gradient או רקע צבעוני, החלף ללבן
          if ((bgImage && bgImage !== 'none' && bgImage.includes('gradient')) || 
              (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent' && bgColor !== 'rgba(255, 255, 255, 1)' && bgColor !== 'rgb(255, 255, 255)')) {
            originalBackgrounds.push({
              element: el,
              background: el.style.background,
              backgroundColor: el.style.backgroundColor,
              backgroundImage: el.style.backgroundImage
            });
            el.style.background = 'white';
            el.style.backgroundColor = 'white';
            el.style.backgroundImage = 'none';
          }
        });
      }
      
      // גם על ה-ReturnsSection עצמו - ודא שהרקע לבן
      const returnsSectionBg = window.getComputedStyle(returnsSectionRef.current).backgroundColor;
      const returnsSectionBgImage = window.getComputedStyle(returnsSectionRef.current).backgroundImage;
      if (returnsSectionBgImage && returnsSectionBgImage !== 'none' && returnsSectionBgImage.includes('gradient')) {
        originalBackgrounds.push({
          element: returnsSectionRef.current,
          background: returnsSectionRef.current.style.background,
          backgroundColor: returnsSectionRef.current.style.backgroundColor,
          backgroundImage: returnsSectionRef.current.style.backgroundImage
        });
        returnsSectionRef.current.style.background = 'white';
        returnsSectionRef.current.style.backgroundColor = 'white';
        returnsSectionRef.current.style.backgroundImage = 'none';
      }
      
      // גם על כל האלמנטים האחרים חוץ מהעיגולים והלגנדה
      const allElements = returnsSectionRef.current.querySelectorAll('*');
      
      allElements.forEach(el => {
        // דלג על העיגולים (neon-card) והלגנדה
        if (el.classList.contains('neon-card') || el.closest('.neon-card') || el === legendContainer || el.closest('[class*="bg-gradient-to-br"][class*="from-blue-50"]')) {
          return;
        }
        
        const computedStyle = window.getComputedStyle(el);
        const bgImage = computedStyle.backgroundImage;
        const bgColor = computedStyle.backgroundColor;
        
        // אם יש gradient או רקע צבעוני, החלף ללבן
        if ((bgImage && bgImage !== 'none' && bgImage.includes('gradient')) || 
            (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent' && bgColor !== 'rgba(255, 255, 255, 1)' && bgColor !== 'rgb(255, 255, 255)')) {
          originalBackgrounds.push({
            element: el,
            background: el.style.background,
            backgroundColor: el.style.backgroundColor,
            backgroundImage: el.style.backgroundImage
          });
          el.style.background = 'white';
          el.style.backgroundColor = 'white';
          el.style.backgroundImage = 'none';
        }
        
        // הגדל את הטקסט "מצטיין טוב ממוצע" מתחת לעיגולים
        // זה הטקסט שמופיע ב-span עם performance.label
        if (el.tagName === 'SPAN' && el.textContent && 
            (el.textContent.trim() === 'מצטיין' || el.textContent.trim() === 'טוב' || 
             el.textContent.trim() === 'ממוצע' || el.textContent.trim() === 'מתחת לממוצע' || 
             el.textContent.trim() === 'חלש')) {
          // ודא שזה הטקסט מתחת לעיגול (בדוק שההורה שלו הוא div עם text-center)
          const parent = el.parentElement;
          if (parent && parent.classList.contains('text-center')) {
            originalPerformanceLabelStyles.push({
              element: el,
              fontSize: el.style.fontSize,
              fontWeight: el.style.fontWeight
            });
            el.style.fontSize = '1rem'; // 16px - גדול יותר מ-text-sm
            el.style.fontWeight = '600';
          }
        }
      });

      // המתן לרינדור מחדש
      await new Promise(resolve => setTimeout(resolve, 500));

      // צלם את הניתוח
      const canvas = await html2canvas(returnsSectionRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: isCurrentlyMobile ? window.innerWidth : 1200,
        windowWidth: isCurrentlyMobile ? window.innerWidth : 1200
      });

      // הסר את הכותרת הזמנית
      if (headerElement.parentNode) {
        headerElement.parentNode.removeChild(headerElement);
      }

      // החזר רוחב מקורי
      returnsSectionRef.current.style.width = originalWidth;
      returnsSectionRef.current.style.minWidth = originalMinWidth;
      returnsSectionRef.current.style.maxWidth = originalMaxWidth;
      
      // החזר את כל הסגנונות המקוריים
      originalStyles.forEach(({ element, overflow, textOverflow, whiteSpace, maxWidth }) => {
        element.style.overflow = overflow;
        element.style.textOverflow = textOverflow;
        element.style.whiteSpace = whiteSpace;
        element.style.maxWidth = maxWidth;
      });
      
      // החזר צבעי כותרות
      originalTitleStyles.forEach(({ element, color, background, webkitBackgroundClip, backgroundClip, webkitTextFillColor, className, zIndex }) => {
        element.style.color = color || '';
        element.style.background = background || '';
        element.style.webkitBackgroundClip = webkitBackgroundClip || '';
        element.style.backgroundClip = backgroundClip || '';
        element.style.webkitTextFillColor = webkitTextFillColor || '';
        element.style.zIndex = zIndex || '';
        element.className = className || '';
        
        // החזר גם את ה-span הפנימי אם יש
        const span = element.querySelector('span');
        if (span) {
          span.style.color = '';
          span.style.webkitTextFillColor = '';
          span.className = span.className.replace(/bg-gradient-to-r|bg-clip-text|text-transparent/g, '').trim();
        }
      });
      
      // החזר צבעי עיגולים והסר style elements
      originalCardStyles.forEach(({ element, border, borderWidth, borderStyle, borderColor, boxShadow, background, backgroundColor }) => {
        element.style.border = border || '';
        element.style.borderWidth = borderWidth || '';
        element.style.borderStyle = borderStyle || '';
        element.style.borderColor = borderColor || '';
        element.style.boxShadow = boxShadow || '';
        element.style.background = background || '';
        element.style.backgroundColor = backgroundColor || '';
      });
      
      // החזר צבעי טקסט בעיגולים
      originalValueStyles.forEach(({ element, color }) => {
        element.style.color = color || '';
      });
      
      // החזר סגנונות rank-badge
      originalRankBadgeStyles.forEach(({ element, position, transform, marginTop, marginBottom, background, backgroundColor, border, borderWidth, borderStyle, borderColor, borderRadius, padding, color, fontWeight }) => {
        element.style.position = position || '';
        element.style.transform = transform || '';
        element.style.marginTop = marginTop || '';
        element.style.marginBottom = marginBottom || '';
        element.style.background = background || '';
        element.style.backgroundColor = backgroundColor || '';
        element.style.border = border || '';
        element.style.borderWidth = borderWidth || '';
        element.style.borderStyle = borderStyle || '';
        element.style.borderColor = borderColor || '';
        element.style.borderRadius = borderRadius || '';
        element.style.padding = padding || '';
        element.style.color = color || '';
        element.style.fontWeight = fontWeight || '';
      });
      
      // החזר רקעים מקוריים
      originalBackgrounds.forEach(({ element, background, backgroundColor, backgroundImage }) => {
        element.style.background = background || '';
        element.style.backgroundColor = backgroundColor || '';
        element.style.backgroundImage = backgroundImage || '';
      });
      
      // החזר גודל פונט של הטקסט "מצטיין טוב ממוצע"
      originalPerformanceLabelStyles.forEach(({ element, fontSize, fontWeight }) => {
        element.style.fontSize = fontSize || '';
        element.style.fontWeight = fontWeight || '';
      });
      
      // החזר רווחים של הכותרת הראשית
      originalMainTitleStyles.forEach(({ element, marginBottom }) => {
        element.style.marginBottom = marginBottom || '';
      });
      
      // הסר את כל ה-style elements
      styleElements.forEach(styleEl => {
        if (styleEl.parentNode) {
          styleEl.parentNode.removeChild(styleEl);
        }
      });
      
      // החזר את כל האלמנטים למצב המקורי
      hiddenElements.forEach(({ element, visibility }) => {
        if (visibility !== undefined) element.style.visibility = visibility;
      });

      // המר לתמונה והורד
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const productName = getDisplayName(product);
        const specializationName = bestMatchingSpec?.title?.split(' - ')[1] || bestMatchingSpec?.title || 'כללי';
        const riskLevel = product.riskLevel?.level || '?';
        const fileName = `תשואות_${productName}_${specializationName}_רמת_סיכון_${riskLevel}_${new Date().toISOString().split('T')[0]}.png`;
        
        link.href = url;
        link.download = fileName;
        link.click();
        
        URL.revokeObjectURL(url);
        
        setExportMessage('התמונה הורדה!');
        setTimeout(() => setExportMessage(''), 2000);
      });

    } catch (error) {
      console.error('שגיאה בייצוא תמונה:', error);
      alert('אירעה שגיאה בהורדת התמונה');
      setExportMessage('');
    }
  };


  // עדכן את הקומפוננטה הראשית עם הרווחים המצומצמים האלה:

return (
  <div ref={returnsSectionRef} className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3 md:p-6 mb-4 md:mb-8 relative overflow-hidden">
    
    {/* רקע דקורטיבי */}
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-purple-50/30"></div>
    
    <div className="relative z-10">
      
      {/* כותרת ראשית - מצומצמת במובייל */}
      <div className="mb-6 md:mb-10">
        <h2 className="text-lg md:text-3xl font-bold text-gray-900 mb-3 md:mb-4 flex items-center gap-2 md:gap-3">
          <TrendingUp className="h-5 w-5 md:h-7 md:w-7 text-blue-600" />
          <span className="text-sm md:text-3xl">  תחקור תשואות עם דירוג באותה רמת הסיכון</span>
          
          <div className="flex items-center gap-2 ml-auto">
            {/* כפתור הורדת תמונה */}
            <button
              onClick={exportReturnsAsImage}
              className="flex items-center justify-center gap-1 px-2 md:px-3 py-2 bg-blue-600 text-white rounded-md md:rounded-lg hover:bg-blue-700 transition-colors text-xs md:text-sm shadow-md hover:shadow-lg"
              title="הורד כתמונה"
            >
              <svg 
                className="h-3 w-3 md:h-4 md:w-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                />
              </svg>
              <span className="hidden sm:inline">הורד תמונה</span>
              <span className="sm:hidden">תמונה</span>
            </button>
            
            {exportMessage && (
              <span data-export-message className="text-xs md:text-sm text-green-600 font-bold">
                {exportMessage}
              </span>
            )}
          
          <button
            ref={tooltipRef}
            onMouseEnter={() => !isMobile && setShowTooltip(true)}
            onMouseLeave={() => !isMobile && setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
              className="p-1 md:p-2 hover:bg-blue-100 rounded-full transition-colors duration-200"
            aria-label="מידע נוסף על מערכת הדירוג"
          >
            <HelpCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-600 hover:text-blue-700" />
          </button>
          </div>
        </h2>
      </div>

      {/* טולטיפ למובייל */}
      {isMobile && showTooltip && (
        <div className="mb-3 animate-in slide-in-from-top duration-300">
          <TooltipContent 
            isMobile={true} 
            onClose={() => setShowTooltip(false)} 
          />
        </div>
      )}

      {/* טולטיפ לדקסטופ */}
      {!isMobile && showTooltip && (
        <TooltipPortal targetRef={tooltipRef}>
          <div className="animate-in fade-in slide-in-from-bottom-4 zoom-in-95 duration-300">
            <TooltipContent 
              isMobile={false} 
              onClose={() => setShowTooltip(false)} 
            />
          </div>
        </TooltipPortal>
      )}
      
      {/* לגנדת הצבעים */}
      <ColorLegend />
      
      {/* דקסטופ - שורה אחת עם סימוני טווח */}
      <div className="hidden md:block">
        {/* סימוני טווח זמן - דקסטופ */}
        <div className="mb-6">
          <div className="grid grid-cols-7 gap-3 lg:gap-4">
            
            {/* טווח קצר - 3 עמודות ראשונות */}
            <div className="col-span-3 relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4">
                <div className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-bold text-green-700">תשואה מצטברת בטווח הקצר</span>
                </div>
              </div>
              <div className="absolute top-2 left-2 right-2 h-0.5 bg-gradient-to-r from-green-300 to-green-200 rounded-full"></div>
            </div>
            
            {/* טווח בינוני - 3 עמודות באמצע */}
            <div className="col-span-3 relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4">
                <div className="inline-flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-bold text-blue-700">תשואה מצטברת בטווח הבינוני</span>
                </div>
              </div>
              <div className="absolute top-2 left-2 right-2 h-0.5 bg-gradient-to-r from-blue-300 to-blue-200 rounded-full"></div>
            </div>
            
            {/* טווח ארוך - עמודה אחרונה */}
            <div className="col-span-1 relative">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 whitespace-nowrap">
                <div className="inline-flex items-center gap-1">
                  <Target className="h-3 w-3 text-purple-600" />
                  <span className="text-xs font-bold text-purple-700">תשואה מצטברת בטווח הארוך</span>
                </div>
              </div>
              <div className="absolute top-2 left-2 right-2 h-0.5 bg-gradient-to-r from-purple-300 to-purple-200 rounded-full"></div>
            </div>
            
          </div>
        </div>

        {/* כרטיסיות בשורה אחת - דקסטופ */}
        <div className="grid grid-cols-7 gap-3 lg:gap-4">
               
       <PeriodCard 
  period="month1"
  title="חודש אחרון"
  index={0}
  ranking={rankings.month1}
  returnValue={product.returns?.month1}
/>

<PeriodCard 
  period="ytd"
  title="מתחילת שנה"
  index={1}
  ranking={rankings.ytd}
  returnValue={product.returns?.ytd}
/>

<PeriodCard 
  period="year1"
  title="שנה"
  index={2}
  ranking={rankings.year1}
  returnValue={product.returns?.year1}
/>

<PeriodCard 
  period="year2"
  title="שנתיים"
  index={3}
  ranking={rankings.year2}
  returnValue={product.returns?.year2}
/>

<PeriodCard 
  period="year3"
  title="3 שנים"
  index={4}
  ranking={rankings.year3}
  returnValue={product.returns?.year3}
/>

<PeriodCard 
  period="year4"
  title="4 שנים"
  index={5}
  ranking={rankings.year4}
  returnValue={product.returns?.year4}
/>

<PeriodCard 
  period="year5"
  title="5 שנים"
  index={6}
  ranking={rankings.year5}
  returnValue={product.returns?.year5}
/>
        </div>
        
      </div>

    <div className="md:hidden space-y-4">
        
        {/* טווח קצר */}
        <div>
          <div className="mb-2">
            <div className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3 text-green-600" />
              <span className="text-xs font-bold text-green-700">תשואה מצטברת בטווח הקצר</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1 pb-6">
            <PeriodCard 
              period="month1"
              title="חודש אחרון"
              index={0}
              ranking={rankings.month1}
              returnValue={product.returns?.month1}
            />
            
            <PeriodCard 
              period="ytd"
              title="מתחילת שנה"
              index={1}
              ranking={rankings.ytd}
              returnValue={product.returns?.ytd}
            />
            
            <PeriodCard 
              period="year1"
              title="שנה"
              index={2}
              ranking={rankings.year1}
              returnValue={product.returns?.year1}
            />
          </div>
        </div>

        {/* טווח בינוני */}
        <div>
          <div className="mb-2">
            <div className="inline-flex items-center gap-1">
              <BarChart3 className="h-3 w-3 text-blue-600" />
              <span className="text-xs font-bold text-blue-700">תשואה מצטברת בטווח הבינוני</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1 pb-6">
            <PeriodCard 
              period="year2"
              title="2 שנים"
              index={3}
              ranking={rankings.year2}
              returnValue={product.returns?.year2}
            />
            
            <PeriodCard 
              period="year3"
              title="3 שנים"
              index={4}
              ranking={rankings.year3}
              returnValue={product.returns?.year3}
            />
            
            <PeriodCard 
              period="year4"
              title="4 שנים"
              index={5}
              ranking={rankings.year4}
              returnValue={product.returns?.year4}
            />
          </div>
        </div>

        {/* טווח ארוך */}
        <div>
          <div className="mb-2">
            <div className="inline-flex items-center gap-1">
              <Target className="h-3 w-3 text-purple-600" />
              <span className="text-xs font-bold text-purple-700">תשואה מצטברת בטווח הארוך</span>
            </div>
          </div>
          <div className="flex justify-center pb-6">
            <PeriodCard 
              period="year5"
              title="5 שנים"
              index={6}
              ranking={rankings.year5}
              returnValue={product.returns?.year5}
            />
          </div>
        </div>

      </div>
        
    </div>
  </div>
);
}
export default ReturnsSection